import { createPartnerAdminApiStore } from '@/api'

const getFilenameFromContentDisposition = (contentDisposition, defaultFilename = '다운로드.pdf') => {
  const matches = contentDisposition.match(/filename\*=UTF-8''([^;]*)/)
  return matches && matches[1] ? decodeURIComponent(matches[1]) : defaultFilename
}

const downloadFile = (data, filename, blobType) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: blobType }))
  const link = document.createElement('a')

  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()

  window.URL.revokeObjectURL(url)
}

const isPngArrayBuffer = (buffer) => {
  // PNG 시그니처: 89 50 4E 47 0D 0A 1A 0A
  const pngSignature = [137, 80, 78, 71, 13, 10, 26, 10];
  const array = new Uint8Array(buffer);
  return pngSignature.every((byte, index) => array[index] === byte);
};

const isPdfArrayBuffer = (buffer) => {
  // PDF 파일은 %PDF-로 시작
  const arr = new Uint8Array(buffer).slice(0, 4);
  const signature = new TextDecoder().decode(arr);
  return signature === '%PDF';
}

const apiStore = createPartnerAdminApiStore([
  {
    action: '_fetchContractHistory',
    path: `v3/partners/{partnerNo}/contracts`,
    pathParams: ['partnerNo'],
    property: 'contractHistory',
    method: 'get',
    cacheable: false,
    onSuccess: (state, payload) => {
      state.contractHistory = payload.data.metrics ?? []
    }
  },
  {
    action: '_downloadContract',
    path: `v3/partners/{partnerNo}/contracts/{seq}`,
    pathParams: ['partnerNo', 'seq'],
    method: 'get',
    cacheable: false,
    requestConfig: {
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/pdf'
      }
    },
  },
])

export default {
  namespaced: true,
  mixins: [apiStore],
  state: {
    contractHistory: []
  },
  actions: {
    // partnerNo: number
    fetchContractHistory({ dispatch }, params) {
      return dispatch('_fetchContractHistory', { params })
    },

    // partnerNo: number, seq: number
    async downloadContract({ dispatch }, { params, fileType }) {
      // 계약서의 파일 확장자에 따라 동적으로 requestConfig 설정
      const requestConfig = {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          // 현재는 pdf, png 계약서만 지원합니다.
          Accept: fileType === 'pdf' ? 'application/pdf' : 'image/png'
        }
      };

      const response = await dispatch('_downloadContract', { params, requestConfig })
      
      /**
       * FIXME: responseType을 `arraybuffer`로 설정했기 때문에 서버에서 내려오는 에러 응답도
       * ArrayBuffer로 변환되어 내려오는 이슈가 있습니다.
       * 올바른 PDF 파일인지 확인하기 위해 isPdfArrayBuffer 함수를 이용해 PDF signature를 확인합니다.
       * 
       * https://github.com/axios/axios/issues/2434
       * https://github.com/axios/axios/issues/1392
       */
      if (fileType === 'pdf' && isPdfArrayBuffer(response.data)) {
        const filename = getFilenameFromContentDisposition(response.headers['content-disposition'] || '')
        downloadFile(response.data, filename, 'application/pdf')
        return
      }

      // TODO: PNG 파일일 경우
      if (fileType === 'png' && isPngArrayBuffer(response.data)) {
        const filename = getFilenameFromContentDisposition(response.headers['content-disposition'] || '');
        downloadFile(response.data, filename, 'image/png')
        return;
      }
      
      // response Error 정보 파싱
      try {
        const text = new TextDecoder().decode(response.data);
        const errorData = JSON.parse(text);
  
        if (errorData.error) {
          throw new Error(`계약서 다운로드에 실패했습니다. code: ${errorData.error.code}, message: ${errorData.error.message}`)
        }
        
        // error 객체가 없는 경우에 대한 처리
        throw new Error('계약서 다운로드에 실패했습니다.')
      } catch (parseError) {
        // JSON.parse 실패 시
        console.error('Error parsing response:', parseError)
        throw new Error('계약서 다운로드 중 오류가 발생했습니다.')
      }
    },
  },
  getters: {
    contractHistory: (state) => {
      return state.contractHistory
    },
  }
}
